import { useEffect, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
    getMyOrganizationsApiV1OrganizationsMyGet,
    getOrganizationApiV1OrganizationsOrgIdGet,
} from '@/common/api/client/sdk.gen'

const ORG_ID_KEY = 'current_org_id'

const orgIdListeners = new Set<() => void>()

const subscribeOrgId = (listener: () => void) => {
    orgIdListeners.add(listener)
    const onStorage = (e: StorageEvent) => {
        if (e.key === ORG_ID_KEY) listener()
    }
    window.addEventListener('storage', onStorage)
    return () => {
        orgIdListeners.delete(listener)
        window.removeEventListener('storage', onStorage)
    }
}

const notifyOrgIdChange = () => {
    orgIdListeners.forEach(l => l())
}

const useCurrentOrgId = (): string | null =>
    useSyncExternalStore(
        subscribeOrgId,
        () => getCurrentOrgId(),
        () => null
    )

const getOrgs = () => getMyOrganizationsApiV1OrganizationsMyGet().then(r => r.data!)

const getOrg = (org_id: string) => getOrganizationApiV1OrganizationsOrgIdGet({ path: { org_id } }).then(r => r.data!)

export const useMyOrgs = () => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['my_orgs'],
        queryFn: () => getOrgs().then(({ items }) => items),
    })
    return { data, isLoading, error }
}

export const useOrg = (org_id: string) => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['orgs', org_id],
        queryFn: () => getOrg(org_id),
    })
    return { data, isLoading, error }
}

export const setCurrentOrgId = (org_id: string): void => {
    try {
        localStorage.setItem(ORG_ID_KEY, org_id)
        notifyOrgIdChange()
    } catch (error) {
        console.error('Error saving org ID to localStorage:', error)
    }
}

export const getCurrentOrgId = (): string | null => {
    try {
        return localStorage.getItem(ORG_ID_KEY)
    } catch (error) {
        console.error('Error reading org ID from localStorage:', error)
        return null
    }
}

export const removeCurrentOrgId = (): void => {
    try {
        localStorage.removeItem(ORG_ID_KEY)
        notifyOrgIdChange()
    } catch (error) {
        console.error('Error removing org ID from localStorage:', error)
    }
}

export const useChangeOrg = () => {
    const queryClient = useQueryClient()

    return async (orgId: string) => {
        const myOrgs = await queryClient.fetchQuery({
            queryKey: ['my_orgs'],
            queryFn: () => getOrgs().then(({ items }) => items),
        })

        if (!myOrgs) {
            throw new Error('Организации не найдены')
        }
        const newOrg = myOrgs.find(org => org.id === orgId)
        if (newOrg) {
            setCurrentOrgId(newOrg.id)
            queryClient.setQueryData(['current_org', newOrg.id], newOrg)
            queryClient.invalidateQueries({ queryKey: ['org_members'] })
            queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
            queryClient.invalidateQueries({ queryKey: ['ozonProducts'] })
            queryClient.invalidateQueries({ queryKey: ['wildberriesProducts'] })
            queryClient.invalidateQueries({ queryKey: ['org_integrations'] })
        } else {
            throw new Error('Organization not found')
        }
    }
}

export const useCurrentOrg = () => {
    const queryClient = useQueryClient()
    const org_id = useCurrentOrgId()
    const { data: myOrgs, isLoading: isMyOrgsLoading } = useMyOrgs()

    useEffect(() => {
        if (!myOrgs || myOrgs.length === 0) return

        const isValidOrg = org_id && myOrgs.some(org => org.id === org_id)
        if (isValidOrg) return

        const firstOrg = myOrgs[0]
        setCurrentOrgId(firstOrg.id)
        queryClient.setQueryData(['current_org', firstOrg.id], firstOrg)
        queryClient.invalidateQueries({ queryKey: ['org_members'] })
        queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
        queryClient.invalidateQueries({ queryKey: ['ozonProducts'] })
        queryClient.invalidateQueries({ queryKey: ['wildberriesProducts'] })
    }, [org_id, myOrgs, queryClient])

    const orgFromList = myOrgs?.find(org => org.id === org_id)

    const currentOrgQuery = useQuery({
        queryKey: ['current_org', org_id],
        queryFn: () => (org_id ? getOrg(org_id) : null),
        enabled: !!org_id && !isMyOrgsLoading && !orgFromList,
        staleTime: 5 * 60 * 1000,
    })

    return {
        ...currentOrgQuery,
        data: orgFromList ?? currentOrgQuery.data,
        isLoading: isMyOrgsLoading || (!orgFromList && currentOrgQuery.isLoading),
    }
}
